import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DocumentUploadDialog } from "@/components/documents/DocumentUploadDialog";
import { CATEGORY_LABELS, documentOpenTarget, openDocument } from "@/components/documents/documentCategories";
import { RecordPanel } from "./RecordFrame";
import { dayLabel, localYmd } from "./recordFormat";

interface RailDoc { id: string; name: string | null; fileName: string; fileUrl: string | null; category: string; createdAt: string }

/** The record's files: newest first, eight then "Show all", open in a new tab, upload with a category. */
export function RailDocuments({ companyId, contactId }: { companyId?: string; contactId?: string }) {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pending, setPending] = useState<RailDoc | null>(null);
  const [showAll, setShowAll] = useState(false);
  const params = new URLSearchParams();
  if (companyId) params.append("companyId", companyId);
  if (contactId) params.append("contactId", contactId);

  const { data: docs = [], isLoading, isError, refetch } = useQuery<RailDoc[]>({
    queryKey: ["/api/documents", companyId, contactId, undefined],
    queryFn: async () => {
      const r = await fetch(`/api/documents?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load documents");
      return r.json();
    },
    enabled: !!(companyId || contactId),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/documents/${id}`, { method: "DELETE", credentials: "include" });
      // Only the uploader or an admin may delete; say so instead of failing quietly.
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Couldn't delete the document");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/documents"] });
      setPending(null);
      toast.success("Document deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const today = localYmd(new Date());
  const shown = showAll ? docs : docs.slice(0, 8);
  return (
    <RecordPanel
      title="Documents"
      count={docs.length}
      action={
        <button type="button" onClick={() => setUploadOpen(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-text hover:underline">
          <Upload className="h-3 w-3" />Upload
        </button>
      }
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : isError ? (
        <p className="text-[12.5px] text-muted-foreground">
          Couldn't load documents.{" "}
          <button type="button" onClick={() => refetch()} className="font-semibold text-primary-text hover:underline">Retry</button>
        </p>
      ) : docs.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">No documents yet.</p>
      ) : (
        <ul className="divide-y">
          {shown.map((d) => {
            const how = documentOpenTarget(d.fileUrl);
            const label = d.name || d.fileName;
            return (
              <li key={d.id} className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 py-1.5">
                <button
                  type="button"
                  disabled={how === "missing"}
                  onClick={() => openDocument(d.fileUrl, d.fileName)}
                  title={how === "missing" ? "This file was never stored. Upload it again." : `Open ${label}`}
                  className="min-w-0 text-left disabled:cursor-not-allowed"
                >
                  <div className="truncate text-[13px] font-semibold group-hover:underline">{label}</div>
                  <div className="flex flex-wrap gap-x-1.5 text-xs text-muted-foreground">
                    <span>{CATEGORY_LABELS[d.category] ?? "Other"}</span>
                    <span>·</span>
                    <span className="tabular-nums">{dayLabel(d.createdAt, today)}</span>
                    {how === "missing" && <span className="text-amber-700 dark:text-amber-400">re-upload needed</span>}
                  </div>
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${label}`}
                  onClick={() => setPending(d)}
                  className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {docs.length > shown.length && (
        <button type="button" onClick={() => setShowAll(true)} className="mt-1.5 text-xs font-semibold text-primary-text hover:underline">
          Show all {docs.length}
        </button>
      )}
      <DocumentUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} target={{ companyId, contactId }} />
      <ConfirmDialog
        open={!!pending}
        onOpenChange={(o) => { if (!o) setPending(null); }}
        title={`Delete ${pending?.name || pending?.fileName || "this document"}?`}
        description="The file is removed from the CRM for everyone. Only the person who uploaded it, or an admin, can delete it."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={remove.isPending}
        onConfirm={() => { if (pending) remove.mutate(pending.id); }}
      />
    </RecordPanel>
  );
}
